import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Grid,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
// import InfoIcon from "@mui/icons-material/Info";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";

import { getCurrentProject } from "../../../../actions/projectsActions";
import {
  getProjectBatchesList,
  createNewProjectBatch,
  deleteCurrentProjectBatch,
  approveProjectBatchAll,
  rejectProjectBatchAll,
  getBatchCalculationsList,
} from "../../../../actions/batchesActions";
import { resetBatchesState } from "../../../../slices/batchesSlice";
import { resetProjectsState } from "../../../../slices/projectsSlice";
import AppPagination from "../../../../components/AppPagination";
import ConfirmationDialog from "../../../../components/ConfirmationDialog";
import BatchApprovalProgress from "../../../../components/BatchApprovalProgress";
import BatchStatusChip from "../../../../components/BatchStatusChip";
import { currencyFormatter } from "../../../../utils/formatUtils";

const periodTypeLabels = {
  daily: "Diario",
  monthly: "Mensual",
  quarterly: "Trimestral",
  annual: "Anual",
};

const statusColors = {
  pending: "warning",
  processing: "info",
  completed: "success",
  failed: "error",
  approved: "success",
  partially_approved: "info",
  rejected: "error",
  cancelled: "default",
};

const statusLabels = {
  pending: "Pendiente",
  processing: "Procesando",
  completed: "Generado",
  failed: "Error",
  approved: "Aprobado",
  partially_approved: "Parcialmente Aprobado",
  rejected: "Rechazado",
  cancelled: "Cancelado",
};

const ProjectBatchesPage = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const project = useSelector((state) => state.projects.current);
  const batches = useSelector((state) => state.batches.list);

  const [page, setPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [markAllReadyDialogOpen, setMarkAllReadyDialogOpen] = useState(false);

  const service = project?.service;

  useEffect(() => {
    dispatch(getCurrentProject({ id: projectId }));
    return () => {
      dispatch(resetBatchesState());
      dispatch(resetProjectsState());
    };
  }, [projectId]);

  useEffect(() => {
    dispatch(getProjectBatchesList({ projectId, query: { page } }));
  }, [projectId, page]);

  const handleBack = () => {
    navigate(`/dashboard/proyectos/${projectId}`);
  };

  const handleRefresh = () => {
    dispatch(getProjectBatchesList({ projectId, query: { page } }));
  };

  const handleCreateBatch = (force = false) => {
    dispatch(
      createNewProjectBatch({
        projectId,
        force,
        callback: () => {
          handleRefresh();
          setConflictDialogOpen(false);
        },
        onConflict: () => {
          setConflictDialogOpen(true);
        },
      })
    );
  };

  const handleConflictConfirm = () => {
    handleCreateBatch(true);
  };

  const handleConflictCancel = () => {
    setConflictDialogOpen(false);
  };

  const handleMenuOpen = (event, batch) => {
    setAnchorEl(event.currentTarget);
    setSelectedBatch(batch);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBatch(null);
  };

  const handleViewDistributions = () => {
    if (selectedBatch) {
      navigate(
        `/dashboard/proyectos/${projectId}/batches/${selectedBatch._id}/pre-distribuciones`
      );
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteConfirm = () => {
    if (selectedBatch) {
      dispatch(
        deleteCurrentProjectBatch({
          projectId,
          batchId: selectedBatch._id,
          callback: () => {
            handleRefresh();
            setDeleteDialogOpen(false);
            setSelectedBatch(null);
          },
        })
      );
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedBatch(null);
  };

  const handleApproveClick = () => {
    setApproveDialogOpen(true);
    setAnchorEl(null);
  };

  const handleApproveConfirm = () => {
    if (selectedBatch) {
      dispatch(
        approveProjectBatchAll({
          projectId,
          batchId: selectedBatch._id,
          notes: "Aprobación masiva desde interfaz de batches",
          callback: () => {
            handleRefresh();
            setApproveDialogOpen(false);
            setSelectedBatch(null);
          },
        })
      );
    }
  };

  const handleApproveCancel = () => {
    setApproveDialogOpen(false);
    setSelectedBatch(null);
  };

  const handleRejectClick = () => {
    setRejectDialogOpen(true);
    setAnchorEl(null);
  };

  const handleRejectConfirm = () => {
    if (selectedBatch) {
      dispatch(
        rejectProjectBatchAll({
          projectId,
          batchId: selectedBatch._id,
          reason: "Rechazo masivo desde interfaz de batches",
          callback: () => {
            handleRefresh();
            setRejectDialogOpen(false);
            setSelectedBatch(null);
          },
        })
      );
    }
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setSelectedBatch(null);
  };

  // const handleSummaryClick = (batch) => {
  //   setSelectedBatch(batch);
  //   setSummaryDialogOpen(true);
  // };

  const handleSummaryClose = () => {
    setSummaryDialogOpen(false);
  };

  const handleMarkAllReadyClick = () => {
    setMarkAllReadyDialogOpen(true);
    setAnchorEl(null);
  };

  const handleMarkAllReadyConfirm = () => {
    if (!selectedBatch) return;

    // Cerrar el diálogo inmediatamente y proceder con la acción
    setMarkAllReadyDialogOpen(false);

    // Obtener todas las pre-distribuciones del batch
    dispatch(
      getBatchCalculationsList({
        projectId,
        batchId: selectedBatch._id,
        query: { page: 1, limit: 1000 }, // Obtener todas
      })
    );

    // Esperar un momento para que se carguen las pre-distribuciones
    setTimeout(() => {
      // Navegar a la página de pre-distribuciones
      navigate(
        `/dashboard/proyectos/${projectId}/batches/${selectedBatch._id}/pre-distribuciones`
      );
    }, 500);
  };

  const handleMarkAllReadyCancel = () => {
    setMarkAllReadyDialogOpen(false);
    setSelectedBatch(null);
  };

  const nextPage = () => setPage(page + 1);
  const prevPage = () => setPage(Math.max(page - 1, 1));

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleTimeString("es-MX", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPeriodDates = (batch) => {
    const { startDate, endDate, type } = batch.period || {};
    if (!startDate || !endDate) return "-";

    if (type === "daily") {
      // Para tipo diario, mostrar fecha y hora
      return `${formatDateShort(startDate)} (${formatTime(
        startDate
      )} - ${formatTime(endDate)})`;
    }
    // Para otros tipos, solo fecha
    return `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            Batches calculados
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Proyecto: {project?.name || projectId}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{ mr: 1 }}
        >
          Actualizar
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleCreateBatch(false)}
          disabled={!service?.enabled}
        >
          Generar Batch
        </Button>
      </Box>

      {service && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Configuración del Servicio
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Estado
                </Typography>
                <Chip
                  label={service.enabled ? "Habilitado" : "Deshabilitado"}
                  color={service.enabled ? "success" : "default"}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Tipo de Período
                </Typography>
                <Typography variant="body1">
                  {periodTypeLabels[service.rules?.period?.type] || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Día del Mes
                </Typography>
                <Typography variant="body1">
                  {service.rules?.period?.dayOfMonth || "-"}
                </Typography>
              </Grid>
              {/* <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Valor de Fórmula
                </Typography>
                <Typography variant="body1">
                  {service.rules?.formula?.value != null
                    ? `${service.rules.formula.value}%`
                    : "-"}
                </Typography>
              </Grid> */}
            </Grid>
          </CardContent>
        </Card>
      )}

      {!service && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography color="text.secondary" textAlign="center">
              No hay servicio configurado para este proyecto.
            </Typography>
          </CardContent>
        </Card>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Período</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Inversiones</TableCell>
              <TableCell align="right">Asociados</TableCell>
              <TableCell align="right">Monto Bruto</TableCell>
              <TableCell align="right">Fees</TableCell>
              <TableCell align="right">Monto Neto</TableCell>
              <TableCell align="center">Pre-distribuciones</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No hay batches calculados
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              batches.map((batch) => {
                const isStrikethrough =
                  batch.status === "error" ||
                  batch.status === "superseded" ||
                  batch.status === "rejected" ||
                  batch.status === "failed";
                return (
                  <TableRow key={batch._id} hover>
                    <TableCell
                      sx={{
                        textDecoration: isStrikethrough
                          ? "line-through"
                          : "none",
                      }}
                    >
                      <Typography variant="body2" fontWeight="medium">
                        {batch.period?.label ||
                          periodTypeLabels[batch.period?.type] ||
                          "-"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {formatPeriodDates(batch)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Creado: {formatDate(batch.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <BatchStatusChip status={batch.status} size="small" />
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        textDecoration: isStrikethrough
                          ? "line-through"
                          : "none",
                      }}
                    >
                      {batch.summary?.totalInvestments || 0}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        textDecoration: isStrikethrough
                          ? "line-through"
                          : "none",
                      }}
                    >
                      {batch.summary?.uniqueAssociates || 0}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        textDecoration: isStrikethrough
                          ? "line-through"
                          : "none",
                      }}
                    >
                      {currencyFormatter.format(
                        batch.summary?.totalGrossAmount,
                        project?.isUSD
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        textDecoration: isStrikethrough
                          ? "line-through"
                          : "none",
                      }}
                    >
                      {currencyFormatter.format(
                        batch.summary?.totalFees,
                        project?.isUSD
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        textDecoration: isStrikethrough
                          ? "line-through"
                          : "none",
                      }}
                    >
                      {currencyFormatter.format(
                        batch.summary?.totalNetAmount,
                        project?.isUSD
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <BatchApprovalProgress
                        summary={batch.summary}
                        size="small"
                        showPercentage={true}
                        showProgressBar={true}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {!(
                        batch.status === "cancelled" ||
                        batch.status === "superseded" ||
                        batch.status === "rejected" ||
                        batch.status === "failed"
                      ) && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, batch)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleViewDistributions}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Ver Pre-distribuciones</ListItemText>
        </MenuItem>
        {selectedBatch?.summary?.readyForApprovalCount > 0 && (
          <MenuItem onClick={handleApproveClick} sx={{ color: "success.main" }}>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Aprobar Todas las Listas</ListItemText>
          </MenuItem>
        )}
        {selectedBatch?.summary?.pendingCount > 0 && (
          <MenuItem
            onClick={handleMarkAllReadyClick}
            sx={{ color: "primary.main" }}
          >
            <ListItemIcon>
              <PlaylistAddCheckIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Marcar Todas como Listas</ListItemText>
          </MenuItem>
        )}
        {!(
          selectedBatch?.status === "approved" ||
          selectedBatch?.status === "rejected" ||
          selectedBatch?.status === "partially_approved"
        ) && (
          <MenuItem onClick={handleRejectClick} sx={{ color: "error.main" }}>
            <ListItemIcon>
              <CancelIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Rechazar Batch</ListItemText>
          </MenuItem>
        )}
        {selectedBatch?.status === "pending" && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Eliminar</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Batch"
        message="¿Estás seguro de que deseas eliminar este batch? Esta acción no se puede deshacer."
      />

      <ConfirmationDialog
        open={conflictDialogOpen}
        onClose={handleConflictCancel}
        onConfirm={handleConflictConfirm}
        title="Batch ya existe"
        message="Ya existe un batch calculado para este período. ¿Deseas generar un nuevo batch de todas formas? El batch anterior será reemplazado."
        confirmText="Confirmar"
        cancelText="Cancelar"
      />

      <ConfirmationDialog
        open={approveDialogOpen}
        onClose={handleApproveCancel}
        onConfirm={handleApproveConfirm}
        title="Aprobar Todas las Pre-Distribuciones Listas"
        message={`¿Estás seguro de que deseas aprobar todas las pre-distribuciones que están listas para aprobación? Se aprobarán ${
          selectedBatch?.summary?.readyForApprovalCount || 0
        } pre-distribuciones. Esta acción creará las distribuciones y reportes finales.`}
        confirmText="Aprobar Todas"
        cancelText="Cancelar"
      />

      <ConfirmationDialog
        open={rejectDialogOpen}
        onClose={handleRejectCancel}
        onConfirm={handleRejectConfirm}
        title="Rechazar Batch"
        message="¿Estás seguro de que deseas rechazar todas las pre-distributions de este batch? Esta acción no se puede deshacer."
        confirmText="Rechazar"
        cancelText="Cancelar"
      />

      <ConfirmationDialog
        open={markAllReadyDialogOpen}
        onClose={handleMarkAllReadyCancel}
        onConfirm={handleMarkAllReadyConfirm}
        title="Ir a Pre-Distribuciones para Marcar Todas"
        message={`Serás redirigido a la página de pre-distribuciones donde podrás marcar todas las ${
          selectedBatch?.summary?.pendingCount || 0
        } pre-distribuciones pendientes como listas para aprobación.`}
        confirmText="Ir a Pre-Distribuciones"
        cancelText="Cancelar"
      />

      <Dialog
        open={summaryDialogOpen}
        onClose={handleSummaryClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Resumen Detallado del Batch
          {selectedBatch && (
            <Typography variant="body2" color="text.secondary">
              {selectedBatch.period?.label ||
                periodTypeLabels[selectedBatch.period?.type] ||
                "-"}{" "}
              | {formatPeriodDates(selectedBatch)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedBatch && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Información General
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Estado
                </Typography>
                <Chip
                  label={
                    statusLabels[selectedBatch.status] || selectedBatch.status
                  }
                  color={statusColors[selectedBatch.status] || "default"}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Fecha de Creación
                </Typography>
                <Typography variant="body1">
                  {formatDate(selectedBatch.createdAt)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Resumen de Cálculo
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Total de Inversiones
                </Typography>
                <Typography variant="h6">
                  {selectedBatch.summary?.totalInvestments || 0}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Asociados Únicos
                </Typography>
                <Typography variant="h6">
                  {selectedBatch.summary?.uniqueAssociates || 0}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Monto Bruto
                </Typography>
                <Typography variant="h6">
                  {currencyFormatter.format(
                    selectedBatch.summary?.totalGrossAmount,
                    project?.isUSD
                  )}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Total de Fees
                </Typography>
                <Typography variant="h6">
                  {currencyFormatter.format(
                    selectedBatch.summary?.totalFees,
                    project?.isUSD
                  )}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Monto Neto
                </Typography>
                <Typography variant="h6" color="primary">
                  {currencyFormatter.format(
                    selectedBatch.summary?.totalNetAmount,
                    project?.isUSD
                  )}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Contadores de Aprobación
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Pendientes
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <Chip
                    label={selectedBatch.summary?.pendingCount || 0}
                    color="warning"
                    size="medium"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Listos para Aprobación
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <Chip
                    label={selectedBatch.summary?.readyForApprovalCount || 0}
                    color="primary"
                    size="medium"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Aprobados
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <Chip
                    label={selectedBatch.summary?.approvedCount || 0}
                    color="success"
                    size="medium"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Rechazados
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <Chip
                    label={selectedBatch.summary?.rejectedCount || 0}
                    color="error"
                    size="medium"
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSummaryClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <AppPagination page={page} prev={prevPage} next={nextPage} />
    </Box>
  );
};

export default ProjectBatchesPage;
